import { useEffect, useMemo, useRef, useState } from "react";
import { useDropDirection } from "src/hooks/useDropDirection";
import { useTranslation } from "react-i18next";
import { TbTrash, TbRefresh, TbPlus, TbChevronDown, TbGripVertical, TbCheck } from "react-icons/tb";
import { allowedFactorRange, displayCode, isFlatFeeCode, searchGoaCatalog, stripPrefixes, type GoaCode } from "src/helpers/goaRules";
import { FactorInput } from "./FactorInput";
import { JustificationField } from "./JustificationField";
import type { JustificationList } from "src/hooks/useJustifications";
import {
    blankDraftRow,
    blankMaterialLine,
    draftLineAmount,
    draftMaterialTotal,
    type GoaLineDraft,
    type MaterialLineDraft,
} from "./goaLineDraft";
import "./goaLineEditor.scss";

const fmt = (n: number) =>
    n.toLocaleString("de-DE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

// Material/expense sub-line kinds — mirrors KIND_LABELS in ExamTable.tsx (excluding
// "B" Begründung, already its own field here, and "K" Keine Berechnung, a PAD-only marker).
const MATERIAL_LINE_KINDS = ["L", "M", "P", "G", "H", "E"] as const;
const MATERIAL_LINE_KIND_KEYS: Record<(typeof MATERIAL_LINE_KINDS)[number], string> = {
    L: "invoicePage.kindMaterial",
    M: "invoicePage.kindMedication",
    P: "invoicePage.kindPostage",
    G: "invoicePage.kindExpertReport",
    H: "invoicePage.kindFee",
    E: "invoicePage.kindCompensation",
};

function CodeField({
    value,
    catalogList,
    catalogByNr,
    duplicate,
    autoFocus,
    onChangeCode,
    onSelectCatalogRow,
}: {
    value: string;
    catalogList: GoaCode[];
    catalogByNr: Map<string, GoaCode>;
    duplicate: boolean;
    autoFocus: boolean;
    onChangeCode: (code: string) => void;
    onSelectCatalogRow: (row: GoaCode) => void;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    // Once the user types here the value is a search query, not a stored code — "e" or "g" must
    // stay visible even though they match a flat-fee flag.
    const [typed, setTyped] = useState(false);
    // Typed since the last pick — Return on an applied code must not reset an edited factor or text.
    const unapplied = useRef(false);
    // Opening a line focuses this field — that shouldn't cover the line with the search list.
    const skipOpenOnFocus = useRef(autoFocus);
    const fieldRef = useRef<HTMLDivElement>(null);

    const trimmed = value.trim();
    const matches = useMemo(() => searchGoaCatalog(catalogList, trimmed), [trimmed, catalogList]);

    const catalogLoaded = catalogByNr.size > 0;
    // A flat-fee code (SKL, SKH, ...) is an internal collision-avoidance id (see
    // pad/createExams.ts / examController.ts's normalizeFlatFeeCode) — never a real GOÄ
    // Ziffer a doctor would recognize or need to see. Showing it here read as "wtf is SKL??";
    // blank the field instead (same treatment the read-only invoice views already give it)
    // and don't flag it as an "unknown code" — it was never meant to look up in the catalog.
    const flatFee = !typed && isFlatFeeCode(value);
    const isUnknown = !flatFee && catalogLoaded && trimmed.length > 0 && !catalogByNr.has(trimmed);
    // Near the bottom of the screen (or above a sticky bottom bar) the list opens upward.
    const direction = useDropDirection(fieldRef, open && matches.length > 0, 230);

    const pick = (row: GoaCode) => {
        unapplied.current = false;
        onSelectCatalogRow(row);
        setOpen(false);
    };

    // Exact codes only: "12" must not silently become the first code starting with it.
    const applyTypedCode = () => {
        const query = trimmed.toLowerCase();
        const exact = catalogList.find((c) => displayCode(c.nr).toLowerCase() === query);
        if (exact && unapplied.current) pick(exact);
    };

    return (
        <div className="goa-line-editor__code-field" ref={fieldRef}>
            <input
                type="text"
                className={`goa-line-editor__field-input${isUnknown ? " goa-line-editor__field-input--unknown" : ""}${duplicate ? " goa-line-editor__field-input--duplicate" : ""}`}
                value={flatFee ? "" : displayCode(value)}
                placeholder={t("invoicePage.examPosition")}
                title={duplicate ? t("invoicePage.duplicateCodeError") : isUnknown ? t("invoicePage.unknownCode") : undefined}
                autoFocus={autoFocus}
                onChange={(e) => {
                    setTyped(true);
                    unapplied.current = true;
                    onChangeCode(e.target.value);
                    setOpen(true);
                }}
                onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    // The template editor is a form — Return here must not save it.
                    e.preventDefault();
                    applyTypedCode();
                }}
                onFocus={(e) => {
                    if (skipOpenOnFocus.current) {
                        skipOpenOnFocus.current = false;
                        // Typing replaces the code, as when tabbing into the field.
                        e.target.select();
                        return;
                    }
                    setOpen(true);
                }}
                onClick={() => setOpen(true)}
                onBlur={() => window.setTimeout(() => setOpen(false), 150)}
            />
            {duplicate && <span className="goa-line-editor__field-hint goa-line-editor__field-hint--warn">{t("invoicePage.duplicateCodeError")}</span>}
            {!duplicate && isUnknown && <span className="goa-line-editor__field-hint goa-line-editor__field-hint--error">{t("invoicePage.unknownCode")}</span>}
            {open && matches.length > 0 && (
                <ul className={`goa-line-editor__code-dropdown${direction === "up" ? " goa-line-editor__code-dropdown--up" : ""}`}>
                    {matches.map((c) => (
                        <li key={c.id} onMouseDown={() => pick(c)}>
                            <span className="goa-line-editor__code-dropdown-nr">{displayCode(c.nr)}</span>
                            <span className="goa-line-editor__code-dropdown-desc">{c.description}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

const ROW_STATUS_MS = 3000;

/**
 * Foot of a line's panel: the confirm button, or — where changes count at once (`live`) — a note
 * that goes away again: "added" when the line first is `complete`, "updated" on every later change.
 */
function RowConfirm({ row, live, complete, onConfirm }: { row: GoaLineDraft; live: boolean; complete: boolean; onConfirm: () => void }) {
    const { t } = useTranslation();
    const [status, setStatus] = useState<"rowAdded" | "rowUpdated" | null>(null);
    const signature = JSON.stringify(row);
    const seen = useRef(signature);
    // Panels mount per opening, so a line that's complete by now was added earlier.
    const added = useRef(complete);

    useEffect(() => {
        if (seen.current === signature) return;
        seen.current = signature;
        if (!complete) {
            setStatus(null);
            return;
        }
        setStatus(added.current ? "rowUpdated" : "rowAdded");
        added.current = true;
        const timer = window.setTimeout(() => setStatus(null), ROW_STATUS_MS);
        return () => window.clearTimeout(timer);
        // Only a change to the line itself: re-running on `complete` would cancel the timer.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    if (live) {
        return (
            <span className="goa-line-editor__added" role="status">
                {status && <><TbCheck aria-hidden="true" /> {t(`goaLineEditor.${status}`)}</>}
            </span>
        );
    }
    return (
        <button type="button" className="goa-line-editor__confirm-btn" disabled={!row.code.trim()} onClick={onConfirm}>
            <TbCheck /> {t("goaLineEditor.confirmRow")}
        </button>
    );
}

/**
 * List of GOÄ service lines for one date: a summary row per line that expands into an edit panel
 * (code search, description, quantity, factor, Begründung, material/expense sub-lines).
 * Controlled — the caller owns `rows` and decides how to save them. Deleting a "new" row removes
 * it; deleting an "existing" row only marks it, so it can be restored until the caller saves.
 *
 * Manual invoice creation also uses: `priceEditable` (an Einfachsatz field, for codes not in the
 * catalog, e.g. analog positions), `rowIssues` (problems per row, shown under its summary even
 * while collapsed), `initialExpandedId`, `focusInitialRow` (that line's code field takes focus too;
 * lines opened later always do), `showAdded` (no confirm button, a line briefly says added/updated
 * instead — only where lines count at once, not where the caller still has to save) and `reorderable`
 * (a grip per row: drag it, or focus it and
 * use ↑/↓). Only where the order is kept — the wizard sends lines in order (`positionsnr`), templates
 * store it; the invoice page's `/exams/batch` doesn't, so it leaves this off.
 * `justifications` (the practice's saved Begründungen — `useJustifications`) turns the Begründung field
 * into a numbered pick list with "save as Begründung".
 */
export function GoaLineEditor({
    rows,
    onRowsChange,
    catalogList,
    catalogByNr,
    duplicateCodes,
    priceEditable = false,
    rowIssues,
    initialExpandedId = null,
    focusInitialRow = false,
    showAdded = false,
    reorderable = false,
    justifications,
}: {
    rows: GoaLineDraft[];
    onRowsChange: (rows: GoaLineDraft[]) => void;
    catalogList: GoaCode[];
    catalogByNr: Map<string, GoaCode>;
    duplicateCodes: Set<string>;
    priceEditable?: boolean;
    rowIssues?: Record<string, string[]>;
    initialExpandedId?: string | null;
    focusInitialRow?: boolean;
    showAdded?: boolean;
    reorderable?: boolean;
    justifications?: JustificationList;
}) {
    const { t } = useTranslation();
    const [expandedId, setExpandedId] = useState<string | null>(initialExpandedId);
    // Read when a line's panel mounts: true from the first line the user opens or adds.
    const [focusOnOpen, setFocusOnOpen] = useState(focusInitialRow);
    // A row is only draggable while its grip is held — a draggable row would also drag from the
    // inputs of its open panel.
    const [armedId, setArmedId] = useState<string | null>(null);
    const [dragId, setDragId] = useState<string | null>(null);
    const [dropAt, setDropAt] = useState<{ id: string; after: boolean } | null>(null);

    const moveRow = (localId: string, toIndex: number) => {
        const from = rows.findIndex((r) => r._localId === localId);
        if (from < 0) return;
        const rest = rows.filter((r) => r._localId !== localId);
        const to = Math.max(0, Math.min(rest.length, toIndex));
        if (to === from) return;
        onRowsChange([...rest.slice(0, to), rows[from], ...rest.slice(to)]);
    };

    const endDrag = () => {
        setArmedId(null);
        setDragId(null);
        setDropAt(null);
    };

    const dropOn = (target: GoaLineDraft) => {
        if (dragId && dropAt && dragId !== target._localId) {
            const rest = rows.filter((r) => r._localId !== dragId);
            const index = rest.findIndex((r) => r._localId === target._localId);
            moveRow(dragId, index + (dropAt.after ? 1 : 0));
        }
        endDrag();
    };

    const updateRow = (localId: string, patch: Partial<GoaLineDraft>) =>
        onRowsChange(rows.map((r) => (r._localId === localId ? { ...r, ...patch } : r)));

    const addRow = () => {
        const row = blankDraftRow();
        onRowsChange([...rows, row]);
        setFocusOnOpen(true);
        setExpandedId(row._localId);
    };

    const toggleDeleteRow = (row: GoaLineDraft) => {
        if (expandedId === row._localId) setExpandedId(null);
        if (row._status === "new") {
            onRowsChange(rows.filter((r) => r._localId !== row._localId));
            return;
        }
        updateRow(row._localId, { _status: row._status === "deleted" ? "existing" : "deleted" });
    };

    const updateMaterialLines = (row: GoaLineDraft, materialLines: MaterialLineDraft[]) =>
        updateRow(row._localId, { materialLines });

    return (
        <div className={`goa-line-editor${reorderable ? " goa-line-editor--reorderable" : ""}`}>
            {rows.length > 0 && <div className="goa-line-editor__list">
                {rows.map((row, index) => {
                    const deleted = row._status === "deleted";
                    const expanded = expandedId === row._localId;
                    const issues = deleted ? [] : (rowIssues?.[row._localId] ?? []);
                    // Unused for a flat-fee row (its Factor field isn't rendered at
                    // all below — see isFlatFeeCode — since it has no real
                    // Steigerungsfaktor concept), so the generic fallback here is
                    // harmless for those rows.
                    const range = allowedFactorRange(row.code, catalogByNr);
                    const amount = draftLineAmount(row);
                    const materialTotal = draftMaterialTotal(row);
                    const dropMark =
                        dropAt?.id === row._localId && dragId !== row._localId
                            ? dropAt.after ? " goa-line-editor__service--drop-after" : " goa-line-editor__service--drop-before"
                            : "";

                    return (
                        <div
                            key={row._localId}
                            className={`goa-line-editor__service${deleted ? " goa-line-editor__service--deleted" : ""}${expanded ? " goa-line-editor__service--expanded" : ""}${issues.length > 0 ? " goa-line-editor__service--invalid" : ""}${dragId === row._localId ? " goa-line-editor__service--dragging" : ""}${dropMark}`}
                            draggable={reorderable && armedId === row._localId}
                            onDragStart={(e) => {
                                e.dataTransfer.effectAllowed = "move";
                                // Firefox only starts a drag that carries data.
                                e.dataTransfer.setData("text/plain", row._localId);
                                setDragId(row._localId);
                            }}
                            onDragOver={(e) => {
                                if (!dragId) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                                const rect = e.currentTarget.getBoundingClientRect();
                                const after = e.clientY > rect.top + rect.height / 2;
                                if (dropAt?.id !== row._localId || dropAt.after !== after) setDropAt({ id: row._localId, after });
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                dropOn(row);
                            }}
                            onDragEnd={endDrag}
                        >
                            {reorderable && (
                                <button
                                    type="button"
                                    className="goa-line-editor__grip"
                                    title={t("goaLineEditor.move")}
                                    aria-label={t("goaLineEditor.moveRow", { position: index + 1, count: rows.length })}
                                    onPointerDown={() => setArmedId(row._localId)}
                                    onPointerUp={() => !dragId && setArmedId(null)}
                                    onKeyDown={(e) => {
                                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                            e.preventDefault();
                                            moveRow(row._localId, index + (e.key === "ArrowUp" ? -1 : 1));
                                            // Moving a focused node in the DOM can drop its focus.
                                            const grip = e.currentTarget;
                                            requestAnimationFrame(() => grip.focus());
                                        }
                                    }}
                                >
                                    <TbGripVertical />
                                </button>
                            )}
                            <button
                                type="button"
                                className="goa-line-editor__summary"
                                onClick={() => {
                                    if (deleted) return;
                                    setFocusOnOpen(true);
                                    setExpandedId(expanded ? null : row._localId);
                                }}
                                disabled={deleted}
                            >
                                <span
                                    className={`goa-line-editor__summary-code${!deleted && duplicateCodes.has(row.code) ? " goa-line-editor__summary-code--warn" : ""}`}
                                >
                                    {isFlatFeeCode(row.code) ? "" : (displayCode(row.code) || "—")}
                                </span>
                                <span className="goa-line-editor__summary-description">
                                    {row.description || <em>{t("invoicePage.addService")}</em>}
                                    {materialTotal > 0 && (
                                        <span className="goa-line-editor__summary-material-badge">
                                            +{fmt(materialTotal)} {t("invoicePage.materialLines")}
                                        </span>
                                    )}
                                </span>
                                <span className="goa-line-editor__summary-qty">{row.quantity}×</span>
                                <span className="goa-line-editor__summary-factor">{isFlatFeeCode(row.code) ? "" : row.factor.toFixed(1)}</span>
                                <span className="goa-line-editor__summary-amount">{fmt(amount)}</span>
                                {!deleted && (
                                    <TbChevronDown
                                        className={`goa-line-editor__summary-chevron${expanded ? " goa-line-editor__summary-chevron--open" : ""}`}
                                    />
                                )}
                            </button>

                            <button
                                type="button"
                                className={`goa-line-editor__row-action${deleted ? " goa-line-editor__row-action--restore" : ""}`}
                                onClick={() => toggleDeleteRow(row)}
                                title={deleted ? t("invoicePage.restoreService") : t("invoicePage.deleteService")}
                                aria-label={deleted ? t("invoicePage.restoreService") : t("invoicePage.deleteService")}
                            >
                                {deleted ? <TbRefresh /> : <TbTrash />}
                            </button>

                            {issues.length > 0 && (
                                <ul className="goa-line-editor__row-issues">
                                    {issues.map((msg) => <li key={msg}>{msg}</li>)}
                                </ul>
                            )}

                            {expanded && !deleted && (
                                <div className="goa-line-editor__panel">
                                    <div className="goa-line-editor__field goa-line-editor__field--code">
                                        <label>{t("invoicePage.examPosition")}</label>
                                        <CodeField
                                            value={row.code}
                                            catalogList={catalogList}
                                            catalogByNr={catalogByNr}
                                            duplicate={duplicateCodes.has(row.code)}
                                            autoFocus={focusOnOpen}
                                            onChangeCode={(code) => updateRow(row._localId, { code })}
                                            onSelectCatalogRow={(c) =>
                                                updateRow(row._localId, {
                                                    code: c.nr,
                                                    description: c.description,
                                                    price: c.euroAmount,
                                                    factor: allowedFactorRange(c.nr, catalogByNr).default,
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="goa-line-editor__field goa-line-editor__field--grow">
                                        <label>{t("invoicePage.examDescription")}</label>
                                        <input
                                            type="text"
                                            className="goa-line-editor__field-input"
                                            value={row.description}
                                            onChange={(e) => updateRow(row._localId, { description: e.target.value })}
                                        />
                                    </div>
                                    <div className="goa-line-editor__field goa-line-editor__field--qty">
                                        <label>{t("invoicePage.examQty")}</label>
                                        <input
                                            type="number"
                                            className="goa-line-editor__field-input"
                                            min={1}
                                            step={1}
                                            value={row.quantity}
                                            onChange={(e) => updateRow(row._localId, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                                        />
                                    </div>
                                    {priceEditable && !isFlatFeeCode(row.code) && (
                                        <div className="goa-line-editor__field goa-line-editor__field--price">
                                            <label htmlFor={`price-${row._localId}`}>{t("goaLineEditor.price")}</label>
                                            <input
                                                id={`price-${row._localId}`}
                                                type="number"
                                                className="goa-line-editor__field-input goa-line-editor__field-input--price"
                                                min={0}
                                                step={0.01}
                                                value={row.price}
                                                onChange={(e) => updateRow(row._localId, { price: Math.max(0, Number(e.target.value) || 0) })}
                                            />
                                        </div>
                                    )}
                                    {!isFlatFeeCode(row.code) && (
                                        <div className="goa-line-editor__field goa-line-editor__field--factor">
                                            <label>{t("invoicePage.examFactor")}</label>
                                            <FactorInput
                                                value={row.factor}
                                                min={range.min}
                                                max={range.max}
                                                onChange={(factor) => updateRow(row._localId, { factor })}
                                            />
                                        </div>
                                    )}
                                    <div className="goa-line-editor__field goa-line-editor__field--grow">
                                        <label>{t("invoicePage.justification")}</label>
                                        <JustificationField
                                            placeholder={
                                                // GOÄ §12(3): above the threshold it's required.
                                                !isFlatFeeCode(row.code) && row.factor > range.threshold
                                                    ? t("goaLineEditor.justificationRequired", { threshold: range.threshold.toLocaleString("de-DE") })
                                                    : t("invoicePage.justificationPlaceholder")
                                            }
                                            value={row.justification}
                                            justifications={justifications}
                                            onChange={(justification) => updateRow(row._localId, { justification })}
                                        />
                                    </div>

                                    {row.annotations.length > 0 && (
                                        <div className="goa-line-editor__field goa-line-editor__field--grow">
                                            <label>{t("invoicePage.originalAnnotations")}</label>
                                            <p className="goa-line-editor__annotations-note">
                                                {row.annotations.map((a) => stripPrefixes(a)).join(", ")}
                                            </p>
                                        </div>
                                    )}

                                    <div className="goa-line-editor__material-section">
                                        <span className="goa-line-editor__material-title">{t("invoicePage.materialLines")}</span>
                                        {row.materialLines.map((ml) => {
                                            const updateLine = (patch: Partial<MaterialLineDraft>) =>
                                                updateMaterialLines(row, row.materialLines.map((l) => (l._localId === ml._localId ? { ...l, ...patch } : l)));
                                            return (
                                                <div key={ml._localId} className="goa-line-editor__material-line">
                                                    <select
                                                        className="goa-line-editor__field-input goa-line-editor__field-input--kind"
                                                        value={ml.kind}
                                                        onChange={(e) => updateLine({ kind: e.target.value })}
                                                    >
                                                        {MATERIAL_LINE_KINDS.map((k) => (
                                                            <option key={k} value={k}>
                                                                {t(MATERIAL_LINE_KIND_KEYS[k])}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        type="text"
                                                        className="goa-line-editor__field-input goa-line-editor__field-input--material-label"
                                                        placeholder={t("invoicePage.examDescription")}
                                                        value={ml.label}
                                                        onChange={(e) => updateLine({ label: e.target.value })}
                                                    />
                                                    <input
                                                        type="number"
                                                        step={0.01}
                                                        min={0}
                                                        className="goa-line-editor__field-input goa-line-editor__field-input--cost"
                                                        placeholder={t("invoicePage.materialLineCost")}
                                                        value={ml.cost}
                                                        onChange={(e) => updateLine({ cost: Number(e.target.value) || 0 })}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="goa-line-editor__row-action"
                                                        onClick={() => updateMaterialLines(row, row.materialLines.filter((l) => l._localId !== ml._localId))}
                                                        title={t("invoicePage.deleteService")}
                                                        aria-label={t("invoicePage.deleteService")}
                                                    >
                                                        <TbTrash />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        <button
                                            type="button"
                                            className="goa-line-editor__add-material-btn"
                                            onClick={() => updateMaterialLines(row, [...row.materialLines, blankMaterialLine()])}
                                        >
                                            <TbPlus /> {t("invoicePage.addMaterialLine")}
                                        </button>
                                    </div>

                                    <div className="goa-line-editor__panel-footer">
                                        <RowConfirm
                                            row={row}
                                            live={showAdded}
                                            complete={!!row.code.trim() && issues.length === 0}
                                            onConfirm={() => setExpandedId(null)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>}
            <button type="button" className="goa-line-editor__add-btn" onClick={addRow}>
                <TbPlus /> {t("invoicePage.addService")}
            </button>
        </div>
    );
}
